import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PropertyCard from "@/components/PropertyCard";
import EditableImage from "@/components/EditableImage";
import { getAuthHeaders } from "@/contexts/AuthContext";
import { ArrowRight, Shield, Star, Clock, Headphones, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_SLIDES = [
  { src: "/hero.jpeg", season: "Summer", label: "Summer Adventures", position: "center 20%" },
  { src: "/hero-kayak.jpeg", season: "Summer", label: "Summer on the Water" },
  { src: "/hero-paddleboard.jpeg", season: "Summer", label: "Summer Relaxation", position: "top" },
  { src: "/hero-sup.jpeg", season: "Summer", label: "Summer Sunsets", position: "top" },
  { src: "/hero-fall.jpeg", season: "Fall", label: "Autumn Colors" },
  { src: "/hero-skiing.jpeg", season: "Winter", label: "Winter Thrills" },
  { src: "/hero-tubing.jpeg", season: "Winter", label: "Winter Fun", position: "top" },
  { src: "/hero-lodge.jpg", season: "Winter", label: "Schuss Village" },
];

const VALUE_PROPS = [
  { icon: Star, title: "Curated Properties", desc: "Every listing is personally vetted to meet our exacting standards for quality and comfort." },
  { icon: Shield, title: "Trusted Experience", desc: "Professional management ensures seamless stays from booking to checkout." },
  { icon: Clock, title: "Flexible Stays", desc: "Whether a weekend escape or extended retreat, we accommodate your schedule." },
  { icon: Headphones, title: "Dedicated Support", desc: "Our concierge team is available around the clock to ensure your satisfaction." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [settings, setSettings] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const timerRef = useRef(null);

  const fetchSettings = useCallback(() => {
    axios.get(`${API}/site-settings`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/listings`).then(res => {
      setFeatured(res.data.filter(l => l.is_featured).slice(0, 3));
    }).catch(() => {});
    fetchSettings();
  }, [fetchSettings]);

  // Auto-advance carousel
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrentSlide(c => (c + 1) % HERO_SLIDES.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, []);

  const goToSlide = (i) => {
    setCurrentSlide(i);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide(c => (c + 1) % HERO_SLIDES.length);
    }, 5000);
  };

  const prevSlide = () => goToSlide((currentSlide - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const nextSlide = () => goToSlide((currentSlide + 1) % HERO_SLIDES.length);

  const aboutImage = settings?.about_image || "https://images.unsplash.com/photo-1758565811272-e79917ca0adc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsaXZpbmclMjByb29tJTIwcG9vbCUyMHZpZXd8ZW58MHx8fHwxNzc2MzA3OTU3fDA&ixlib=rb-4.1.0&q=85";
  const slide = HERO_SLIDES[currentSlide];

  const updateSetting = async (key, url) => {
    try {
      const updated = { ...settings, [key]: url };
      await axios.put(`${API}/admin/site-settings`, updated, { headers: getAuthHeaders(), withCredentials: true });
      setSettings(updated);
    } catch { /* handled by EditableImage */ }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Carousel */}
      <section data-testid="hero-section" className="relative h-screen overflow-hidden">
        {/* Slides */}
        {HERO_SLIDES.map((s, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            style={{ opacity: i === currentSlide ? 1 : 0, zIndex: i === currentSlide ? 1 : 0, backgroundColor: s.bg || '#000' }}
          >
            <img
              src={s.src}
              alt={s.label}
              className={`w-full h-full ${s.fit === 'contain' ? 'object-contain' : 'object-cover'}`}
              style={s.position ? { objectPosition: s.position } : {}}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/40 z-[5]" />

        {/* Content */}
        <div className="absolute top-0 left-0 right-0 z-10 px-6 md:px-12 lg:px-24 pt-28 md:pt-32">
          <div className="max-w-3xl">
            <p className="text-base sm:text-lg uppercase tracking-[0.3em] text-white font-bold mb-6" style={{textShadow: '0 2px 6px rgba(0,0,0,0.7)'}}>
              Premium Vacation Rentals
            </p>
            <h1 className="font-['DM_Serif_Display'] text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none text-white mb-8">
              Exceptional Stays by<br />
              <span>K10 Properties Group</span>
            </h1>
            <p className="text-white/80 text-base md:text-lg max-w-xl mb-12">
              Discover hand-selected vacation rentals that redefine comfort and relaxation.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/properties"
                data-testid="hero-view-properties"
                className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-8 py-3.5 uppercase tracking-widest text-sm inline-flex items-center gap-3 rounded"
              >
                View Properties <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-us"
                className="border border-white text-white hover:bg-white hover:text-[#1A1A2E] transition-colors duration-300 px-8 py-3.5 uppercase tracking-widest text-sm rounded font-semibold"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>

        {/* Season Label */}
        <div className="absolute bottom-20 left-6 md:left-12 lg:left-24 z-10">
          <span className="text-xs uppercase tracking-[0.3em] text-white/70" style={{textShadow: '0 1px 3px rgba(0,0,0,0.5)'}}>
            {slide.season} — {slide.label}
          </span>
        </div>

        {/* Navigation Arrows */}
        <button onClick={prevSlide} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 text-white/50 hover:text-white transition-colors" data-testid="hero-prev">
          <ChevronLeft className="w-10 h-10" />
        </button>
        <button onClick={nextSlide} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 text-white/50 hover:text-white transition-colors" data-testid="hero-next">
          <ChevronRight className="w-10 h-10" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3" data-testid="hero-dots">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`transition-all duration-300 rounded-full ${
                i === currentSlide
                  ? "w-8 h-2 bg-white"
                  : "w-2 h-2 bg-white/40 hover:bg-white/70"
              }`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      </section>

      {/* Featured Properties */}
      {featured.length > 0 && (
        <section data-testid="featured-section" className="px-6 md:px-12 lg:px-24 py-24 bg-white">
          <div className="flex items-end justify-between mb-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#32A334] mb-3">Portfolio</p>
              <h2 className="font-['DM_Serif_Display'] text-4xl sm:text-5xl tracking-tight text-[#1A1A2E]">Featured Properties</h2>
            </div>
            <Link
              to="/properties"
              data-testid="featured-view-all"
              className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-[#64748B] hover:text-[#32A334] transition-colors"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((listing) => (
              <PropertyCard key={listing.id} listing={listing} />
            ))}
          </div>
        </section>
      )}

      {/* Why K10 */}
      <section data-testid="why-k10-section" className="px-6 md:px-12 lg:px-24 py-24 bg-[#F4F6FA]">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#32A334] mb-3">Why Choose Us</p>
          <h2 className="font-['DM_Serif_Display'] text-4xl sm:text-5xl tracking-tight text-[#1A1A2E]">The K10 Difference</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {VALUE_PROPS.map((vp, i) => (
            <div key={i} className="p-8 bg-white border border-[#E2E8F0] rounded-lg hover:shadow-md hover:border-[#32A334]/20 transition-all duration-500">
              <vp.icon className="w-8 h-8 text-[#32A334] mb-6" strokeWidth={1.5} />
              <h3 className="font-['DM_Serif_Display'] text-xl font-semibold text-[#1A1A2E] mb-3">{vp.title}</h3>
              <p className="text-[#64748B] text-sm leading-relaxed">{vp.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About Preview */}
      <section data-testid="about-preview-section" className="px-6 md:px-12 lg:px-24 py-24 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <p className="text-xs uppercase tracking-[0.3em] text-[#32A334] mb-3">About Us</p>
            <h2 className="font-['DM_Serif_Display'] text-4xl sm:text-5xl tracking-tight text-[#1A1A2E] mb-6">
              Hospitality,<br />Redefined.
            </h2>
            <p className="text-[#64748B] text-base leading-relaxed mb-8">
              K10 Properties Group curates premium short-term rental experiences for discerning travelers. Every property in our portfolio is chosen for its exceptional quality, prime location, and ability to create lasting memories.
            </p>
            <Link
              to="/about"
              data-testid="about-preview-link"
              className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-8 py-3 uppercase tracking-widest text-sm inline-flex items-center gap-3 rounded"
            >
              Learn More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-6">
            <div className="relative aspect-[4/3] overflow-hidden rounded-lg">
              <EditableImage
                src={aboutImage}
                alt="Luxury interior"
                onReplace={(url) => updateSetting("about_image", url)}
              >
                <img src={aboutImage} alt="Luxury interior" loading="lazy" className="w-full h-full object-cover" />
              </EditableImage>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="px-6 md:px-12 lg:px-24 py-24 bg-[#1A1A2E] text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-[#4CBF4E] mb-3">Ready to Book?</p>
        <h2 className="font-['DM_Serif_Display'] text-4xl sm:text-5xl tracking-tight text-white mb-8">
          Your Next Stay Awaits
        </h2>
        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/properties"
            data-testid="cta-browse"
            className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-8 py-3.5 uppercase tracking-widest text-sm rounded"
          >
            Browse Properties
          </Link>
          <Link
            to="/contact"
            data-testid="cta-contact"
            className="border border-white/30 text-white hover:bg-white/10 transition-colors duration-300 px-8 py-3.5 uppercase tracking-widest text-sm rounded"
          >
            Get In Touch
          </Link>
        </div>
      </section>
    </div>
  );
}
